import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { listCalls, type CallStatus } from "../api/calls.ts";

const STATUSES: CallStatus[] = ["completed", "in_progress", "voicemail", "no_answer", "busy", "failed", "canceled"];

export default function CallsPage() {
  const [statusFilter, setStatusFilter] = useState<CallStatus | undefined>(undefined);
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["calls", statusFilter, q],
    queryFn: () => listCalls({ status: statusFilter, q: q || undefined, limit: 100 }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Calls</h1>
        <input className="input w-full sm:max-w-xs" placeholder="Search by phone..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={chip(!statusFilter)} onClick={() => setStatusFilter(undefined)}>All</button>
        {STATUSES.map((s) => (
          <button key={s} type="button" className={chip(statusFilter === s)} onClick={() => setStatusFilter(s)}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="table-wrapper">
        <table className="table-base">
          <thead>
            <tr>
              <th className="hidden sm:table-cell">When</th>
              <th className="hidden md:table-cell">Direction</th>
              <th className="">From</th>
              <th className="hidden md:table-cell">To</th>
              <th className="hidden md:table-cell">Duration</th>
              <th className="">Status</th>
              <th className="">Recording</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No calls yet. When Twilio is configured and someone calls your number, the call will land here.</td></tr>
            )}
            {data?.items.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="hidden px-4 py-2 text-xs text-slate-500 sm:table-cell md:text-sm">{new Date(c.started_at).toLocaleString()}</td>
                <td className="hidden px-4 py-2 md:table-cell">{c.direction}</td>
                <td className="px-4 py-2">
                  <Link to={`/calls/${c.id}`} className="font-mono text-xs text-brand-700 hover:underline">
                    {c.from_e164}
                  </Link>
                  <div className="text-[10px] text-slate-500 sm:hidden">
                    {new Date(c.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </td>
                <td className="hidden px-4 py-2 font-mono text-xs md:table-cell">{c.to_e164}</td>
                <td className="hidden px-4 py-2 md:table-cell">{formatDuration(c.duration_s)}</td>
                <td className="px-4 py-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${statusClass(c.status)}`}>{c.status.replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-2">
                  {c.recording_id ? (
                    <Link to={`/calls/${c.id}`} className="text-brand-700 hover:underline">play ▸</Link>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function chip(active: boolean) {
  return active
    ? "rounded-full bg-brand-600 px-3 py-1 text-sm text-white"
    : "rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50";
}

function formatDuration(s: number | null): string {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function statusClass(s: string): string {
  switch (s) {
    case "completed":
      return "bg-emerald-100 text-emerald-700";
    case "in_progress":
    case "ringing":
    case "queued":
      return "bg-amber-100 text-amber-700";
    case "voicemail":
      return "bg-violet-100 text-violet-700";
    case "no_answer":
    case "busy":
    case "canceled":
    case "failed":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
}
